/* tslint:disable */
/* eslint-disable */
/**
 * Business Radar
 * The Business Radar API provides powerful tools to screen & monitor companies using news & compliance, allowing you to integrate real-time insights into your own applications.   #### Getting Started To use this API, you need an API Key. Please contact [support@businessradar.com](mailto:support@businessradar.com) to request your key. Once you have a key, you can enter it by clicking the **Authorize** button on the right to start making requests directly from this documentation.  #### Authentication The API key can be provided through the `Authorization` header when making raw requests. For our SDKs, the API key can also be saved as an environmental variable. In this documentation, each endpoint shows the full request, including headers, when invoked.  #### SDKs We offer officially supported SDKs to help you get started quickly: - **Python**: [businessradar-sdk-python](https://github.com/businessradar/businessradar-sdk-python) - **TypeScript / JavaScript**: [businessradar-sdk-typescript](https://github.com/businessradar/businessradar-sdk-typescript) - **PHP**: [businessradar-sdk-php](https://github.com/businessradar/businessradar-sdk-php)  #### Pagination The API uses cursor-based pagination for high performance and reliability. When a request returns more results than the page limit, a `next_key` is included in the response. To fetch the next set of results, include this `next_key` as a query parameter in your next request. If `next_key` is `null`, it means you have reached the end of the available results. The `total_results` field provides the total count of matches found for your query.  #### Asynchronous Processing Several endpoints in the Business Radar API are asynchronous. This means that once a request is posted, Business Radar processes it in the background. To check the progress and/or retrieve the final result, you can use the equivalent GET endpoint for that resource.  #### Webhooks Webhooks allow Business Radar to push real-time event notifications to your application the moment something changes, without polling. When an event occurs, Business Radar sends an HTTP POST request containing a JSON payload to the URL you registered.  **Payload envelope**  Every delivery body carries an `event_type` field identifying the subscription that triggered it, alongside the event-specific payload under a named field (see the table below):  ```json {\"event_type\": \"compliance_check.status_changed\", \"compliance_check\": { ... }} ```  `event_type` always matches the subscription, and the payload field (e.g. `compliance_check`, `registration`) holds the event data.  **Setup**  Create a webhook and subscribe to one or more event types via the `/ext/v3/webhooks/` endpoints. Each webhook has a secret used to sign deliveries so you can verify they originated from Business Radar.  **Event types**  | Event type | Trigger | Payload field | Field schema | |---|---|---|---| | `compliance_check.status_changed` | Every status update on a compliance check | `compliance_check` | [ComplianceCheckList](#model-ComplianceCheckList) | | `compliance_check.status_completed` | Compliance check reaches `completed` status | `compliance_check` | [ComplianceCheckList](#model-ComplianceCheckList) | | `compliance_check.results.new` | New compliance results identified during monitoring | `compliance_check` | [ComplianceCheckList](#model-ComplianceCheckList) | | `company_registration.status_changed` | Every status update on a company registration | `registration` | [Registration](#model-Registration) | | `company_registration.status_registered` | Registration reaches `registered` status | `registration` | [Registration](#model-Registration) | | `company.updated` | A tracked company\'s attributes change (e.g. a D&B refresh) | `company`, `attribute_changes` | [Company](#model-Company), [CompanyAttributeChange](#model-CompanyAttributeChange) |  `company.updated` subscriptions are **portfolio-scoped**: provide a `portfolio` when subscribing, and the event fires only for changes to companies in that portfolio.  **Payload verification**  Every delivery includes an `X-Signature-SHA256` header of the form `sha256=<hex>`. To verify authenticity, compute `HMAC-SHA256` over the raw request body using your webhook secret and compare it to the value after `sha256=`. Reject any delivery where the signatures do not match.  The secret is shown once when you create the webhook and once each time you call the `regenerate_secret` endpoint. **Rotating the secret immediately invalidates the old one** — update your verification code with the new value before rotating, or deliveries will start failing signature checks.  <details> <summary>Python verification example</summary>  ```python import hashlib import hmac  def verify_signature(raw_body: bytes, secret: str, header: str) -> bool:     \"\"\"Return True if the X-Signature-SHA256 header is valid.\"\"\"     digest = hmac.new(secret.encode(), raw_body, hashlib.sha256)     expected = digest.hexdigest()     received = header.removeprefix(\"sha256=\")     return hmac.compare_digest(expected, received) ```  In a Django view:  ```python import json  def webhook(request):     if not verify_signature(         request.body,         secret=YOUR_WEBHOOK_SECRET,         header=request.headers.get(\"X-Signature-SHA256\", \"\"),     ):         return HttpResponse(status=400)     payload = json.loads(request.body)     event_type = payload[\"event_type\"]     # payload also holds the event-specific field, e.g. payload[\"compliance_check\"]     # process event ... ```  </details>  **Retries**  If your endpoint does not respond with a 2xx status code, Business Radar retries the delivery up to 3 times with exponential back-off. After all retries are exhausted the delivery is marked `failed`. Deliveries are deduplicated — the same event is never delivered more than once per subscription.
 *
 * The version of the OpenAPI document: 0.0.1 (general)
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import { Configuration } from "./configuration";
import { RequiredError, RequestArgs } from "./base";
import { AxiosInstance, AxiosResponse } from 'axios';

/**
 *
 * @export
 */
export const DUMMY_BASE_URL = 'https://example.com'

/**
 *
 * @throws {RequiredError}
 * @export
 */
export const assertParamExists = function (functionName: string, paramName: string, paramValue: unknown) {
    if (paramValue === null || paramValue === undefined) {
        throw new RequiredError(paramName, `Required parameter ${paramName} was null or undefined when calling ${functionName}.`);
    }
}

/**
 *
 * @export
 */
export const setApiKeyToObject = async function (object: any, keyParamName: string, configuration?: Configuration) {
    if (configuration && configuration.apiKey) {
        const localVarApiKeyValue = typeof configuration.apiKey === 'function'
            ? await configuration.apiKey(keyParamName)
            : await configuration.apiKey;
        object[keyParamName] = localVarApiKeyValue;
    }
}

/**
 *
 * @export
 */
export const setBasicAuthToObject = function (object: any, configuration?: Configuration) {
    if (configuration && (configuration.username || configuration.password)) {
        object["auth"] = { username: configuration.username, password: configuration.password };
    }
}

/**
 *
 * @export
 */
export const setBearerAuthToObject = async function (object: any, configuration?: Configuration) {
    if (configuration && configuration.accessToken) {
        const accessToken = typeof configuration.accessToken === 'function'
            ? await configuration.accessToken()
            : await configuration.accessToken;
        object["Authorization"] = "Bearer " + accessToken;
    }
}

/**
 *
 * @export
 */
export const setOAuthToObject = async function (object: any, name: string, scopes: string[], configuration?: Configuration) {
    if (configuration && configuration.accessToken) {
        const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
            ? await configuration.accessToken(name, scopes)
            : await configuration.accessToken;
        object["Authorization"] = "Bearer " + localVarAccessTokenValue;
    }
}

/**
 *
 * @export
 */
export const setSearchParams = function (url: URL, ...objects: any[]) {
    const searchParams = new URLSearchParams(url.search);
    for (const object of objects) {
        for (const key in object) {
            if (Array.isArray(object[key])) {
                searchParams.delete(key);
                for (const item of object[key]) {
                    searchParams.append(key, item);
                }
            } else {
                searchParams.set(key, object[key]);
            }
        }
    }
    url.search = searchParams.toString();
}

/**
 *
 * @export
 */
export const serializeDataIfNeeded = function (value: any, requestOptions: any, configuration?: Configuration) {
    const nonString = typeof value !== 'string';
    const needsSerialization = nonString && configuration && configuration.isJsonMime
        ? configuration.isJsonMime(requestOptions.headers['Content-Type'])
        : nonString;
    return needsSerialization
        ? JSON.stringify(value !== undefined ? value : {})
        : (value || "");
}

/**
 *
 * @export
 */
export const toPathString = function (url: URL) {
    return url.pathname + url.search + url.hash
}

/**
 *
 * @export
 */
export const createRequestFunction = function (axiosArgs: RequestArgs, globalAxios: AxiosInstance, BASE_PATH: string, configuration?: Configuration) {
    return <T = unknown, R = AxiosResponse<T>>(axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
        const axiosRequestArgs = {...axiosArgs.options, url: (configuration?.basePath || basePath) + axiosArgs.url};
        return axios.request<T, R>(axiosRequestArgs);
    };
}
